import { useState, useEffect, useCallback } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import type { QuestTemplateBaseFirestore, QuestProgressType } from '../../services/questTypes';

export interface CommonQuestFormData {
  isPublished: boolean;
  isFree: boolean;
  isPrimary: boolean;
  isOptional: boolean;
  isPrerequisite: boolean;
  progressType: QuestProgressType;
  pillText: string;
  textTemplatingPrompt: string;
}

interface UseQuestFormOptions<T extends QuestTemplateBaseFirestore> {
  questTemplate?: T;
  initialOrder: number;
  defaultProgressType?: QuestProgressType;
}

interface UseQuestFormReturn<T extends QuestTemplateBaseFirestore> {
  // Common form state
  commonFormData: CommonQuestFormData;
  loading: boolean;
  error: string | null;

  // Common form handlers
  handleCommonChange: (field: keyof CommonQuestFormData, value: string | boolean) => void;
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;

  // Validation
  validateCommonFields: () => string | null;

  // Save helper
  createBaseQuestTemplate: () => Partial<T>;

  // Form submission wrapper
  handleSubmit: (
    specificValidation: () => string | null,
    createQuestTemplate: (baseData: Partial<T>) => T,
    onSave: (questTemplate: T) => void | Promise<void>
  ) => (e: React.FormEvent) => Promise<void>;

  // Save without closing wrapper
  handleSaveAndContinue: (
    specificValidation: () => string | null,
    createQuestTemplate: (baseData: Partial<T>) => T,
    onSaveAndContinue: (questTemplate: T) => void | Promise<void>
  ) => () => Promise<void>;
}

export const useQuestForm = <T extends QuestTemplateBaseFirestore>({
  questTemplate,
  initialOrder,
  defaultProgressType = 'fixed',
}: UseQuestFormOptions<T>): UseQuestFormReturn<T> => {
  const [commonFormData, setCommonFormData] = useState<CommonQuestFormData>({
    isPublished: false,
    isFree: false,
    isPrimary: false,
    isOptional: false,
    isPrerequisite: false,
    progressType: defaultProgressType,
    pillText: '',
    textTemplatingPrompt: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize common form data from quest template
  useEffect(() => {
    if (questTemplate && questTemplate.id) {
      setCommonFormData({
        isPublished: questTemplate.isPublished,
        isFree: questTemplate.isFree,
        isPrimary: questTemplate.isPrimary,
        isOptional: questTemplate.isOptional,
        isPrerequisite: questTemplate.isPrerequisite,
        progressType: questTemplate.progressType,
        pillText: questTemplate.pillText,
        textTemplatingPrompt: questTemplate.textTemplatingPrompt,
      });
    }
  }, [questTemplate]);

  const handleCommonChange = useCallback((field: keyof CommonQuestFormData, value: string | boolean) => {
    setCommonFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const validateCommonFields = useCallback((): string | null => {
    if (!commonFormData.pillText.trim()) {
      return 'Pill text is required';
    }
    if (!commonFormData.textTemplatingPrompt.trim()) {
      return 'Text templating prompt is required';
    }
    return null;
  }, [commonFormData]);

  const createBaseQuestTemplate = useCallback((): Partial<T> => {
    return {
      id: questTemplate?.id,
      order: initialOrder,
      isPublished: commonFormData.isPublished,
      isFree: commonFormData.isFree,
      isPrimary: commonFormData.isPrimary,
      isOptional: commonFormData.isOptional,
      isPrerequisite: commonFormData.isPrerequisite,
      progressType: commonFormData.progressType,
      pillText: commonFormData.pillText,
      textTemplatingPrompt: commonFormData.textTemplatingPrompt,
      createdAt: questTemplate?.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp(),
    } as Partial<T>;
  }, [questTemplate, initialOrder, commonFormData]);

  const handleSubmit = useCallback(
    (
      specificValidation: () => string | null,
      createQuestTemplate: (baseData: Partial<T>) => T,
      onSave: (questTemplate: T) => void | Promise<void>
    ) => {
      return async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        // Validate common fields first
        const commonValidationError = validateCommonFields();
        if (commonValidationError) {
          setError(commonValidationError);
          setLoading(false);
          return;
        }

        // Then validate specific fields
        const specificValidationError = specificValidation();
        if (specificValidationError) {
          setError(specificValidationError);
          setLoading(false);
          return;
        }

        try {
          const baseData = createBaseQuestTemplate();
          const questTemplateData = createQuestTemplate(baseData);
          const result = onSave(questTemplateData);
          // Handle both sync and async onSave functions
          if (result && typeof result.then === 'function') {
            await result;
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to save quest template');
        } finally {
          setLoading(false);
        }
      };
    },
    [validateCommonFields, createBaseQuestTemplate]
  );

  const handleSaveAndContinue = useCallback(
    (
      specificValidation: () => string | null,
      createQuestTemplate: (baseData: Partial<T>) => T,
      onSaveAndContinue: (questTemplate: T) => void | Promise<void>
    ) => {
      return async () => {
        setLoading(true);
        setError(null);

        // Validate common fields first
        const commonValidationError = validateCommonFields();
        if (commonValidationError) {
          setError(commonValidationError);
          setLoading(false);
          return;
        }

        // Then validate specific fields
        const specificValidationError = specificValidation();
        if (specificValidationError) {
          setError(specificValidationError);
          setLoading(false);
          return;
        }

        try {
          const baseData = createBaseQuestTemplate();
          const questTemplateData = createQuestTemplate(baseData);
          const result = onSaveAndContinue(questTemplateData);
          // Handle both sync and async onSaveAndContinue functions
          if (result && typeof result.then === 'function') {
            await result;
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to save quest template');
        } finally {
          setLoading(false);
        }
      };
    },
    [validateCommonFields, createBaseQuestTemplate]
  );

  return {
    commonFormData,
    loading,
    error,
    handleCommonChange,
    setError,
    setLoading,
    validateCommonFields,
    createBaseQuestTemplate,
    handleSubmit,
    handleSaveAndContinue,
  };
};
